import { Component } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { ErrorService } from '@kv-common-components/kv-common-components';
import { I18nPipe } from '@shared/pipes/i18n.pipe';
import { modalAnimations } from '@shared/animations/animations';
import * as moment from 'moment';

@Component({
    selector: 'app-error-modal-component',
    templateUrl: './error-modal.component.html',
    styleUrls: [],
    animations: modalAnimations,
})
export class ErrorModalComponent {
    title: string;
    closebutton: string;
    errorMessage: string;
    errorCodeMessage: string;
    private defaultErrorMessage =
        'Nous vous invitons à réessayer ultérieurement.';

    constructor(
        private errorService: ErrorService,
        private i18nPipe: I18nPipe,
        private logger: NGXLogger
    ) {
        this.errorService.getError().subscribe((errorData) => {
            this.handleErrorData(errorData);
        });
    }

    getErrorMessage(error: any) {
        if (error) {
            this.logger.trace('Received error : ', error);
            this.setDefaultValues();

            if (this.i18nPipe.i18nData) {
                // Erreur fonctionnelle : mapping code-message via bundles
                this.getFunctionnalError(error);
            }
            // Erreur technique : toujours traitée, même si bundles non chargés
            this.getTechnicalError(error);
        }
    }
    close() {
        this.errorMessage = null;
    }
    setDefaultValues() {
        this.logger.trace('Setting default error message');
        this.errorCodeMessage = null;
        if (this.i18nPipe.i18nData) {
            this.title = this.i18nPipe.transform('global.popin.error.title');
            this.errorMessage = this.i18nPipe.transform('ws.error.unknown');
            this.closebutton = this.i18nPipe.transform('global.close');
        } else {
            this.title = 'Une erreur est survenue';
            this.errorMessage = this.defaultErrorMessage;
            this.closebutton = 'Fermer';
        }
    }

    /**
     * Point d'entrée unique pour gérer toutes les formes d'erreur
     */
    async handleErrorData(errorData: any) {
        let finalError = errorData;

        // Cas null ou undefined direct
        if (finalError == null) {
            this.logger.trace('Erreur reçue : null ou undefined');
            return;
        }

        // Cas HttpErrorResponse avec Blob
        if (this.isHttpErrorResponseWithBlob(finalError)) {
            try {
                const text = await finalError.error.text();
                finalError = JSON.parse(text);
            } catch (parseErr) {
                this.logger.trace(
                    'Erreur lors du parsing du Blob dans error',
                    parseErr
                );
                this.setDefaultValues();
                return;
            }
        }
        // Cas Blob direct
        else if (this.isBlob(finalError)) {
            try {
                const text = await finalError.text();
                finalError = JSON.parse(text);
            } catch (parseErr) {
                this.logger.trace('Erreur lors du parsing du Blob', parseErr);
                this.setDefaultValues();
                return;
            }
        }
        // Cas chaîne JSON
        else if (this.isJSONString(finalError)) {
            try {
                finalError = JSON.parse(finalError);
            } catch (parseErr) {
                this.logger.trace('Erreur parsing chaîne JSON', parseErr);
                this.setDefaultValues();
                return;
            }
        }

        // Cas HttpErrorResponse Angular ou objet d’erreur imbriqué
        if (this.isHttpErrorResponseAngular(finalError)) {
            finalError = finalError.error;
        }

        if (finalError) {
            this.getErrorMessage(finalError);
        } else {
            this.setDefaultValues();
        }
    }

    isHttpErrorResponseWithBlob(finalError: any): boolean {
        return (
            finalError !== null &&
            typeof finalError === 'object' &&
            'error' in finalError &&
            finalError.error !== null &&
            typeof finalError.error === 'object' &&
            typeof finalError.error.text === 'function'
        );
    }

    isBlob(finalError: any): boolean {
        return (
            finalError !== null &&
            typeof finalError === 'object' &&
            typeof finalError.text === 'function'
        );
    }

    isJSONString(finalError: any): boolean {
        return (
            typeof finalError === 'string' && finalError.trim().startsWith('{')
        );
    }

    isHttpErrorResponseAngular(finalError: any): boolean {
        return (
            finalError !== null &&
            typeof finalError === 'object' &&
            'error' in finalError &&
            finalError.error !== null &&
            typeof finalError.error !== 'function'
        );
    }

    getErrorCodeMessage(handledError: any) {
        if (handledError.code) {
            const codeStr = handledError.code.toString();
            // Regex pour détecter la presence d'une date JJ/MM/AAAA ou JJ-MM-AAAA
            const dateRegex = /\b\d{2}[-/]\d{2}[-/]\d{4}\b/;

            if (dateRegex.test(codeStr)) {
                // La date est déjà présente dans le code, on n'ajoute rien
                this.errorCodeMessage = 'CODE ERREUR : ' + codeStr;
            } else {
                // Sinon, on ajoute la date
                const currentDate = moment(new Date()).format(
                    'DD-MM-YYYY HH:mm:ss'
                );
                this.errorCodeMessage =
                    'CODE ERREUR : ' + codeStr + ' (' + currentDate + ')';
            }
        }
    }

    private getFunctionnalError(error: any): void {
        if (error.erreursFonctionnelle && error.erreursFonctionnelle[0]) {
            const errorMessage = (
                this.i18nPipe.transform(
                    `ws.error.${error.erreursFonctionnelle[0].code.toString()}`
                ) || this.defaultErrorMessage
            )
                // filter error code at the end of the message
                .replace(/<[a-zA-z0-9_]*>/, '')
                .trim();
            if (errorMessage) {
                this.errorMessage = errorMessage;
            }
            this.getErrorCodeMessage(error.erreursFonctionnelle[0]);
        }
    }

    private getTechnicalError(error: any): void {
        if (
            (error.code && error.code === 401) ||
            (error.status && error.status === 401)
        ) {
            const errorMessage = this.i18nPipe.transform(
                'ws.error.401.badInfo'
            );
            if (errorMessage) {
                this.errorMessage = errorMessage;
            }
        } else if (error.erreurTechnique?.code) {
            this.errorMessage = this.defaultErrorMessage;
        }

        if (error.erreurTechnique?.code) {
            this.getErrorCodeMessage(error.erreurTechnique);
        }
    }
}
